import { planFillEmptySlot, planLockPositions, planSwap } from '../board';
import { canLock, isInMatch } from '../machine';
import { ok, reject } from '../result';
import type {
  BoardOp,
  BoardSwapOp,
  ConfirmLockCommand,
  ConfirmLockSnapshot,
  Decision,
  SwapBoardTileCommand,
  SwapBoardTileSnapshot,
} from './types';

/**
 * LOCK-4 — `ConfirmLock`, the one command in build unit U-4 that may write a `boardPosition`.
 *
 * ── Why this is in U-4 at all, when U-9 owns the lock flow ───────────────────
 *
 * **Declared thin slice**, on the U-0…U-6 precedent. LOCK-1/LOCK-3's nudge and review screen are
 * U-9's and are not built here. What is built is the *handler*, because without it INV-4's
 * compile-time claim is vacuous: "only `commands/board.ts` handlers carry `boardPosition`" says
 * nothing if `commands/board.ts` does not exist. `handleConfirmLock` returning `Decision<BoardOp>`
 * against `handleIncrement` returning `Decision<CaptureOp>` is what makes the separation a fact a
 * compiler checks rather than a sentence in a design document.
 *
 * ── Three enforcement layers, none redundant ─────────────────────────────────
 *
 * | Layer | Catches |
 * |---|---|
 * | `canLock(state, phase)` here | a second lock, and a lock from a state that has no board (INV-4: "assigned exactly once per lock") |
 * | `matches.confirmLock` in the repo | the same thing again, on the committed row, because a UI guard is not an invariant |
 * | the `boardCap` postcondition + the unique `&[matchId+boardPosition]` index | a 9th tile and two tiles in one slot (INV-5, LIVE-8) |
 *
 * The middle row exists because this handler reads a snapshot; the repo re-reads inside the
 * transaction. Under the single-flight queue those cannot diverge, but the repo check is what holds
 * on the import path, which does not go through this handler at all.
 */

const MODULE = 'commands/board';

/**
 * LOCK-4 — assign `boardPosition` 0..n-1 in the confirmed order, set `capturePhase='locked'`,
 * record `lockTs`.
 *
 * PRE-7 and LOCK-7 both land on `planLockPositions`: fewer than `BOARD_CAP` selections is legal and
 * produces a contiguous prefix, so the render is a partition-and-sort and an empty slot is an
 * explicit empty state rather than a hole in a position-indexed grid.
 */
export function handleConfirmLock(
  cmd: ConfirmLockCommand,
  snap: ConfirmLockSnapshot,
): Decision<BoardOp> {
  const match = snap.match;
  if (match === undefined) {
    return reject(MODULE, cmd.matchId, 'ERR_MATCH_NOT_FOUND', 'That match no longer exists.');
  }

  if (!canLock(match.state, match.capturePhase)) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_LOCK_NOT_PERMITTED',
      match.capturePhase === 'locked'
        ? 'This board is already locked; positions are assigned exactly once (INV-4).'
        : `A board cannot be locked from ${match.state}.`,
      { state: match.state, capturePhase: match.capturePhase },
    );
  }

  const foreign = cmd.orderedHypothesisIds.filter(
    (id) => !snap.hypotheses.some((row) => row.id === id),
  );
  if (foreign.length > 0) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_HYPOTHESIS_NOT_FOUND',
      'The lock selection names hypotheses that are not in this match.',
      { missing: foreign },
    );
  }

  const plan = planLockPositions(cmd.orderedHypothesisIds, snap.hypotheses);
  if (!plan.ok) return plan;

  return ok({
    ops: [
      { t: 'setBoardPositions', matchId: cmd.matchId, writes: plan.value },
      { t: 'confirmLock', id: cmd.matchId, lockTs: snap.now },
    ],
    log: [
      {
        module: MODULE,
        level: 'info',
        matchId: cmd.matchId,
        msg: `board locked with ${plan.value.length} position write(s)`,
        data: { selected: cmd.orderedHypothesisIds.length },
      },
    ],
    // INV-5 / LIVE-8. `writePositions` already aborts above the cap; declaring it here means the
    // bus asserts it too, so a future op that touches positions without going through that repo
    // function is still caught before commit.
    post: [{ t: 'boardCap', matchId: cmd.matchId }],
  });
}

/**
 * LOCK-6 / DD-3 / HT-2 — the **only** post-lock composition change (build unit U-13b).
 *
 * > LOCK-6: *"Post-lock board composition changes only via Halftime swaps or flag promotion, each an
 * > explicit user action assigning a position and, where the board is full, explicitly displacing a
 * > named tile back to list-only. **No displacement is ever silent.**"*
 *
 * ── The window is the in-match states, and it opened for a reason (U-14a-ii) ─
 *
 * U-13b set it at `halftime` alone, because LOCK-6 names two occasions and the sheet that drove
 * this command only mounted at the break. It also wrote down, correctly, that FLAG-3's *other* arm
 * — promotion at **Triage** (§10.1), which may also assign a position — would need *"an explicit
 * widening with its own reasoning"*, because *"a guard that already admitted a state nothing
 * dispatches from would be a permission granted by accident"*. This is that widening, and its
 * reasoning, with the surface that drives it landing in the same change.
 *
 * §2.3a's *Review flags* row reaches the Triage screen from a half, and §2.3 means the promote
 * dispatched there arrives carrying `firstHalf` or `secondHalf`. §10.1 rules that FLAG-3's optional
 * board position is **kept** on that path rather than narrowed to list-only:
 *
 * > *LOCK-6 names **flag promotion** — not "flag promotion at halftime" — as one of the two legal
 * > post-lock composition changes … the narrowing would forfeit a MUST to buy nothing.*
 *
 * **What buys "nothing" is a mechanism, not an assurance.** Both paths through this handler are
 * incapable of moving an active tile, and that is checkable one call down rather than promised here:
 * `planFillEmptySlot` emits exactly **one** write, `{incoming, filled}` — the contiguous tail, so no
 * existing row is named — and `planSwap` emits exactly **two**, the outgoing to `null` and the
 * incoming to *the outgoing's own position*, so no gap is opened and no third row is named. LIVE-12
 * prohibits **count-driven** reordering, which neither of these is.
 *
 * ── Why the window is `isInMatch` and not `isInPlay ∪ halftime` spelled out ──
 *
 * They are the same set (§2's groupings), and the named predicate is the one that stays correct if
 * the grouping ever changes. What it deliberately does **not** admit is the post-match states: a
 * board is a live capture surface, and `fullTime`/`triage`/`distill` have none — so the Triage
 * screen offers no placement there, and this refuses it if something ever tries. Window and surface
 * are the same shape, which is the property U-13b's note was protecting.
 *
 * ── `capturePhase` must be `locked`, which is not the same guard ─────────────
 *
 * A board only exists once positions have been assigned. In Discovery there is nothing to swap
 * *with*, and assigning a position there would be a second writer against INV-4's *"assigned exactly
 * once per lock or per explicit user swap"* — the lock has not happened yet, so this would be the
 * first. HT-3's *"Lock my 8"* card is the path from that state, which is why §7.4 adds it rather
 * than substituting it.
 *
 * ── Three rejections that are the requirement, not defensive coding ──────────
 *
 * | Refusal | What it prevents |
 * |---|---|
 * | incoming id not in this match | `planSwap` asks only whether the incoming row *holds a position*, which a non-existent id trivially does not — so a foreign or deleted id would sail through and write a slot for a row nothing renders |
 * | `outgoingId === null` on a full board | the handler choosing a victim, i.e. exactly the silent displacement LOCK-6 forbids |
 * | `outgoingId` naming an unpositioned row | a "displacement" that takes nothing off, leaving the board over cap on the next fill |
 *
 * The last two are `planSwap`/`planFillEmptySlot`'s own, reused rather than restated (§6.2's
 * allocation rule lives there and must have one implementation).
 */
export function handleSwapBoardTile(
  cmd: SwapBoardTileCommand,
  snap: SwapBoardTileSnapshot,
): Decision<BoardSwapOp> {
  const match = snap.match;
  if (match === undefined) {
    return reject(MODULE, cmd.matchId, 'ERR_MATCH_NOT_FOUND', 'That match no longer exists.');
  }

  // Re-read **inside the transaction**, never trusted from the surface: a swap dispatched from a
  // stale render after Full Time has landed must be refused by the state it actually finds.
  if (!isInMatch(match.state)) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_NOT_AT_HALFTIME',
      'The board can only be re-composed while the match is running.',
      { state: match.state },
    );
  }

  if (match.capturePhase !== 'locked') {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_BOARD_NOT_LOCKED',
      'There is no board yet. Lock your 8 first.',
      { capturePhase: match.capturePhase },
    );
  }

  if (!snap.hypotheses.some((row) => row.id === cmd.incomingId)) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_HYPOTHESIS_NOT_FOUND',
      'That pattern is not in this match.',
      { incomingId: cmd.incomingId },
    );
  }

  if (cmd.outgoingId !== null && !snap.hypotheses.some((row) => row.id === cmd.outgoingId)) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_HYPOTHESIS_NOT_FOUND',
      'The tile being taken off is not in this match.',
      { outgoingId: cmd.outgoingId },
    );
  }

  const plan =
    cmd.outgoingId === null
      ? planFillEmptySlot(snap.hypotheses, cmd.incomingId)
      : planSwap(snap.hypotheses, cmd.outgoingId, cmd.incomingId);
  if (!plan.ok) return plan;

  return ok({
    ops: [{ t: 'setBoardPositions', matchId: cmd.matchId, writes: plan.value }],
    log: [
      {
        module: MODULE,
        level: 'info',
        matchId: cmd.matchId,
        msg:
          cmd.outgoingId === null
            ? 'LOCK-6: tile added to an empty board slot'
            : 'LOCK-6: board tile swapped, displacement named by the user',
        data: { incomingId: cmd.incomingId, outgoingId: cmd.outgoingId },
      },
    ],
    // INV-5 / LIVE-8 — net-zero by construction on the swap path, but asserted anyway: the whole
    // point of a postcondition is that it does not depend on the plan being the one we think it is.
    post: [{ t: 'boardCap', matchId: cmd.matchId }],
  });
}
